// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import RichTextEditor from "../../common/RichTextEditor";
import { htmlForApi } from "../../../utils/richText";

// Variables
window.Swal = Swal;

// API endpoint
const ADD_BANK_PROJECT_ACCOUNT = "https://ultima.icsgr.com/api/data_management/bank_project_accounts/";
const GET_PROJECTS = "https://ultima.icsgr.com/api/data_management/all_projects/";
const GET_CLIENTS = "https://ultima.icsgr.com/api/data_management/all_clients/";
const GET_BANK_CLIENT_ACCOUNTS = "https://ultima.icsgr.com/api/data_management/all_bank_client_accounts/";

function AddBankProjectAccountModal({ onCreated, onBankProjectAccountCreated, refreshData, defaultProjectId, defaultClientId, lockProject = false, lockClient = false, redirectOnCreate = false }) {
  const [show, setShow] = useState(false);

  // Dropdowns
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [bankClientAccounts, setBankClientAccounts] = useState([]);

  // Form state
  const [projectId, setProjectId] = useState(""); // required
  const [clientId, setClientId] = useState(""); // required
  const [bankClientAccoId, setBankClientAccoId] = useState(""); // optional
  const [notes, setNotes] = useState(""); // optional

  const resetForm = () => {
    setProjectId("");
    setClientId("");
    setBankClientAccoId("");
    setNotes("");
  };

  const handleClose = () => setShow(false);
  const handleShow = () => {
    resetForm();
    if (defaultProjectId) setProjectId(defaultProjectId);
    if (defaultClientId) setClientId(defaultClientId);
    setShow(true);
  };

  // Load dropdowns when modal opens
  useEffect(() => {
    if (show) {
      loadDropdowns();
    }
  }, [show]);

  const loadDropdowns = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken"),
      };
      const [projectsRes, clientsRes, bankClientAccosRes] = await Promise.all([
        axios.get(GET_PROJECTS, { headers: currentHeaders }),
        axios.get(GET_CLIENTS, { headers: currentHeaders }),
        axios.get(GET_BANK_CLIENT_ACCOUNTS, { headers: currentHeaders }),
      ]);

      setProjects(projectsRes?.data?.all_projects || []);
      setClients(clientsRes?.data?.all_clients || []);
      setBankClientAccounts(bankClientAccosRes?.data?.all_bank_client_accounts || []);
    } catch (error) {
      console.error("Dropdowns load error:", error);
      setProjects([]);
      setClients([]);
      setBankClientAccounts([]);
    }
  };

  // Filter bank client accounts by selected client
  const filteredBankClientAccounts = Array.isArray(bankClientAccounts)
    ? bankClientAccounts.filter((a) => {
        const accountClientId = a.client?.client_id || a.client_id;
        return clientId ? accountClientId === clientId : false;
      })
    : [];

  // Validation
  const isProjectValid = projectId !== "";
  const isClientValid = clientId !== "";
  const isFormValid = isProjectValid && isClientValid;

  const createNew = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken"),
      };

      const payload = {
        project_id: projectId,
        client_id: clientId,
        bankclientacco_id: bankClientAccoId || "",
        notes: htmlForApi(notes),
      };

      const res = await axios.post(ADD_BANK_PROJECT_ACCOUNT, payload, { headers: currentHeaders });
      if (res.status === 201) {
        try { if (typeof onCreated === 'function') onCreated(res.data); } catch (_) {}
        try { if (typeof onBankProjectAccountCreated === 'function') onBankProjectAccountCreated(res.data); } catch (_) {}
        try { if (typeof refreshData === 'function') refreshData(); } catch (_) {}
        handleClose();
        if (redirectOnCreate && res?.data?.bankprojacco_id) {
          window.location.href = `/data_management/bank_project_account/${res.data.bankprojacco_id}`;
        }
      }
    } catch (e) {
      console.error("Create bank project account error:", e);
      let errorMessage = "An error occurred while creating the bank project account.";
      if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.response?.data) {
        const errors = e.response.data;
        errorMessage = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
      }
      Swal.fire({ icon: "error", title: "Error", text: errorMessage });
    }
  };

  return (
    <>
      <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
        <BiPlus style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }} />
        Create new Bank Project Account
      </Button>

      <GuardedModal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create new Bank Project Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <SelectWithInlineCreate
                  className="mb-3"
                  label="Project *:"
                  required
                  value={projectId}
                  onChange={setProjectId}
                  disabled={lockProject}
                  placeholderOption="Select Project"
                  overviewLinkEntity="project"
                >
                  {Array.isArray(projects) && projects.map((p) => (
                    <option key={p.project_id} value={p.project_id} title={p.project_id}>
                      {p.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SelectWithInlineCreate
                  className="mb-3"
                  label="Client *:"
                  required
                  value={clientId}
                  onChange={(v) => {
                    setClientId(v);
                    setBankClientAccoId("");
                  }}
                  disabled={lockClient}
                  placeholderOption="Select Client"
                  overviewLinkEntity="client"
                >
                  {Array.isArray(clients) && clients.map((c) => (
                    <option key={c.client_id} value={c.client_id} title={c.client_id}>
                      {c.fullname || `${c.surname || ""} ${c.name || ""}`.trim() || c.client_id}
                    </option>
                  ))}
                </SelectWithInlineCreate>
              </Col>
              <Col md={6}>
                <SelectWithInlineCreate
                  className="mb-3"
                  label="Bank Client Account:"
                  value={bankClientAccoId}
                  onChange={setBankClientAccoId}
                  placeholderOption="Select Bank Client Account (optional)"
                  overviewLinkEntity="bank_client_account"
                >
                  {filteredBankClientAccounts.map((a) => (
                    <option key={a.bankclientacco_id} value={a.bankclientacco_id} title={a.bankclientacco_id}>
                      {[a.bank?.bankname, a.accountnumber].filter(Boolean).join(" · ") || a.bankclientacco_id}
                    </option>
                  ))}
                </SelectWithInlineCreate>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes:</Form.Label>
              <RichTextEditor rows={3} value={notes} onChange={setNotes} placeholder="Notes (optional)" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "red" }}>
                {!isProjectValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Project is required.</li>
                )}
                {!isClientValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Client is required.</li>
                )}
              </ul>
            ) : (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "green" }}>
                <li><AiOutlineCheckCircle style={{ fontSize: 18, marginRight: 6 }} /> Validated</li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>Close</Button>
          <Button color="green" onClick={createNew} disabled={!isFormValid}>Save Changes</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export default AddBankProjectAccountModal;
